import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import LogoHeader from '../components/LogoHeader';
import { setResultData } from '../thunks/test/actions';
import axios from 'axios';
import getAuthToken from '../utils/getAuthToken';

const QuestionReviewPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { sessionId, examId } = useParams();
  
  // Get result data from Redux
  const { resultData } = useSelector(state => state.test);
  const [loading, setLoading] = useState(!resultData);
  const [error, setError] = useState(null);
  
  // Question navigation state
  const [selectedSection, setSelectedSection] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [showPalette, setShowPalette] = useState(false);

  useEffect(() => {
    const fetchResult = async () => {
      if (resultData) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const token = getAuthToken();
        
        if (!token) {
          setError('Authentication required');
          setLoading(false);
          return;
        }

        // Hardcoded sessionId and examId for testing
        const sessionIdToUse = "687cdd321aa94ec3d3eb6b31_1754230096980_ahtemt1mi";
        const examIdToUse = "687cdd321aa94ec3d3eb6b31";

        const response = await axios.get(`/result/${sessionIdToUse}/${examIdToUse}`, {
          headers: {
            'Authorization': `${token}`
          }
        });

        dispatch(setResultData(response.data));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching result:', err);
        setError(err.response?.data?.message || 'Failed to load result');
        setLoading(false);
      }
    };

    fetchResult();
  }, [dispatch, resultData]);

  const formatTime = (milliseconds) => {
    if (milliseconds <= 0) return '0m 0s';
    const minutes = Math.floor(milliseconds / 60000);
    const seconds = Math.floor((milliseconds % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getQuestionStatusIcon = (question) => {
    if (question.isSkipped) return '⏭️';
    if (question.isCorrect) return '✅';
    return '❌';
  };

  const getQuestionStatusColor = (question) => {
    if (question.isSkipped) return 'text-gray-500 bg-gray-50';
    if (question.isCorrect) return 'text-green-600 bg-green-50';
    return 'text-red-600 bg-red-50';
  };

  const nextQuestion = () => {
    if (resultData && resultData.sections[selectedSection]) {
      const maxQuestion = resultData.sections[selectedSection].questions.length - 1;
      if (currentQuestion < maxQuestion) {
        setCurrentQuestion(currentQuestion + 1);
      }
    }
  };

  const previousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const switchToSection = (sectionIndex) => {
    setSelectedSection(sectionIndex);
    setCurrentQuestion(0);
  };

  const goToQuestion = (questionIndex) => {
    setCurrentQuestion(questionIndex);
    setShowPalette(false);
  };

  const handleBackToResults = () => {
    navigate('/result');
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
        <LogoHeader />
        <div className="max-w-6xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200 border-t-blue-600 mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading Question Review</h2>
            <p className="text-gray-600">Please wait while we load your exam results...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100">
        <LogoHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">Unable to Load Results</h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">{error}</p>
            <button 
              onClick={() => navigate('/result')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
            >
              Back to Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!resultData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-100">
        <LogoHeader />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-3">No Result Data</h2>
            <p className="text-gray-600 mb-6">Please go back to results page to load your exam data.</p>
            <button 
              onClick={() => navigate('/result')}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition-colors"
            >
              Back to Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestionData = resultData.sections[selectedSection]?.questions[currentQuestion];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <LogoHeader />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Question Review</h1>
            <p className="text-gray-600">{resultData.examTitle}</p>
          </div>
          <button
            onClick={handleBackToResults}
            className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
          >
            Back to Results
          </button>
        </div>

        {/* Section Navigation */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="flex space-x-1">
            {resultData.sections.map((section, index) => (
              <button
                key={index}
                onClick={() => switchToSection(index)}
                className={`flex-1 px-4 py-3 text-sm font-medium rounded-lg transition-all duration-200 ${
                  selectedSection === index
                    ? 'bg-blue-600 text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <div className="text-center">
                  <div className="font-semibold">{section.sectionName}</div>
                  <div className="text-xs opacity-75">{section.totalQuestions} questions</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Question Navigation */}
        <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <button
                onClick={previousQuestion}
                disabled={currentQuestion === 0}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                Previous
              </button>
              <span className="text-lg font-semibold">
                Question {currentQuestion + 1} of {resultData.sections[selectedSection].questions.length}
              </span>
              <button
                onClick={nextQuestion}
                disabled={currentQuestion === resultData.sections[selectedSection].questions.length - 1}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
              >
                Next
              </button>
            </div>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Time Spent:</span>
                <span className="font-semibold text-blue-600">
                  {formatTime(currentQuestionData?.timeSpent || 0)}
                </span>
              </div>
              <button
                onClick={() => setShowPalette(!showPalette)}
                className="px-4 py-2 bg-purple-100 hover:bg-purple-200 text-purple-700 rounded-lg transition-colors"
              >
                Question Palette
              </button>
            </div>
          </div>
        </div>

        {/* Question Palette */}
        {showPalette && (
          <div className="bg-white rounded-xl shadow-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Question Palette</h3>
            <div className="grid grid-cols-5 md:grid-cols-10 gap-2">
              {resultData.sections[selectedSection].questions.map((question, index) => (
                <button
                  key={index}
                  onClick={() => goToQuestion(index)}
                  className={`w-12 h-12 rounded-lg text-sm font-medium transition-all duration-200 ${
                    index === currentQuestion
                      ? 'bg-blue-600 text-white'
                      : question.isCorrect
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : question.isSkipped
                      ? 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                      : 'bg-red-100 text-red-700 hover:bg-red-200'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Question Card */}
        {currentQuestionData && (
          <div className="bg-white rounded-xl shadow-lg p-6">
            <div className="flex justify-between items-start mb-6">
              <div className="flex-1">
                <div className="flex items-center space-x-3 mb-4">
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                    Q{currentQuestion + 1}
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-medium ${getQuestionStatusColor(currentQuestionData)}`}>
                    {getQuestionStatusIcon(currentQuestionData)} {
                      currentQuestionData.isCorrect ? 'Correct' :
                      currentQuestionData.isSkipped ? 'Skipped' : 'Incorrect'
                    }
                  </span>
                </div>
                <h2 
                  className="text-lg font-medium text-gray-900 leading-relaxed" 
                  dangerouslySetInnerHTML={{ __html: currentQuestionData.en.value }} 
                />
              </div>
            </div>

            <div className="space-y-3">
              {currentQuestionData.en.options.map((option, index) => {
                const isCorrect = currentQuestionData.correctAnswer === option.prompt;
                const isSelected = currentQuestionData.selectedOption === option.prompt;
                const isWrong = isSelected && !isCorrect;
                
                return (
                  <div
                    key={index}
                    className={`flex items-start space-x-3 p-4 rounded-lg border-2 ${
                      isCorrect ? 'bg-green-50 border-green-300' :
                      isWrong ? 'bg-red-50 border-red-300' :
                      'bg-white border-gray-200'
                    }`}
                  >
                    <div className={`mt-1 w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      isCorrect ? 'bg-green-500 border-green-500' :
                      isWrong ? 'bg-red-500 border-red-500' :
                      'bg-gray-300 border-gray-300'
                    }`}>
                      {isCorrect && <span className="text-white text-xs">✓</span>}
                      {isWrong && <span className="text-white text-xs">✗</span>}
                    </div>
                    <span className={`flex-1 ${
                      isCorrect ? 'text-green-700 font-medium' :
                      isWrong ? 'text-red-700 font-medium' :
                      'text-gray-900'
                    }`}>
                      {String.fromCharCode(65 + index)}. {option.value}
                    </span>
                    {isCorrect && <span className="text-green-600 text-lg">✓</span>}
                    {isWrong && <span className="text-red-600 text-lg">✗</span>}
                  </div>
                );
              })}
            </div>

            {/* Question Analytics */}
            <div className="mt-8 bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-4">Question Analytics</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {formatTime(currentQuestionData.timeSpent)}
                  </div>
                  <div className="text-xs text-gray-600">Time Spent</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-purple-600">
                    {currentQuestionData.visitCount}
                  </div>
                  <div className="text-xs text-gray-600">Visits</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-orange-600">
                    {currentQuestionData.optionChangesHistory?.length || 0}
                  </div>
                  <div className="text-xs text-gray-600">Answer Changes</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-indigo-600">
                    {formatTime(currentQuestionData.averageTimePerVisit)}
                  </div>
                  <div className="text-xs text-gray-600">Avg Time/Visit</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuestionReviewPage; 